import { useCallback, useEffect, useState } from 'react'

import { isWebPushEligible, subscribeToWebPush } from '../lib/webPush'

type WebPushStatus = 'idle' | 'subscribing' | 'subscribed' | 'error'

type UseWebPushResult = {
  /** 通知有効化の操作（ボタン）をユーザーへ提示すべきかどうか。 */
  shouldOffer: boolean
  status: WebPushStatus
  /** 許可リクエスト＋購読を実行する。iOS の要件によりユーザージェスチャーから呼ぶこと。 */
  enable: () => Promise<void>
}

function notificationPermission(): NotificationPermission | null {
  if (typeof Notification === 'undefined') return null
  return Notification.permission
}

/**
 * Web Push 対応ブラウザのオプトイン状態を管理する。
 *
 * 許可済み(granted)の場合はサイレントに再購読する（一度許可されればジェスチャー不要）。
 * これにより再インストールやサーバ側削除の後も backend の購読が同期される。未許可の場合は
 * 呼び出し側がボタンを提示し、許可プロンプトがユーザージェスチャー内で発火するようにする
 * （iOS の要件）。
 */
export function useWebPush(enabled: boolean): UseWebPushResult {
  const [eligible, setEligible] = useState(false)
  const [status, setStatus] = useState<WebPushStatus>('idle')
  const [permission, setPermission] = useState<NotificationPermission | null>(
    notificationPermission,
  )

  const subscribe = useCallback(async () => {
    setStatus('subscribing')
    try {
      const subscribed = await subscribeToWebPush()
      setStatus(subscribed ? 'subscribed' : 'idle')
    } catch {
      setStatus('error')
    } finally {
      setPermission(notificationPermission())
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setEligible(false)
      return
    }

    const isEligible = isWebPushEligible()
    setEligible(isEligible)
    setPermission(notificationPermission())

    if (isEligible && notificationPermission() === 'granted') {
      void subscribe()
    }
  }, [enabled, subscribe])

  // error 時は permission に関わらず提示を続けて再試行できるようにする。
  // 特に granted 後の購読失敗（通信断など）はバナーが消えると無言になり、
  // 「有効化したのに通知が来ない」状態を作ってしまう。
  const shouldOffer =
    eligible &&
    status !== 'subscribed' &&
    status !== 'subscribing' &&
    (permission === 'default' || status === 'error')

  return {
    shouldOffer,
    status,
    enable: subscribe,
  }
}
